"use client";

import { Skeleton } from "@mui/material";
import Grid from "@mui/material/Grid2";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";

import MDBox from "@/components/atoms/MDBox";
import PageContainer from "@/components/atoms/PageContainer";
import AlertBlock from "@/components/molecules/AlertBlock";
import NavigationList from "@/components/molecules/NavigationList";
import PageLayout from "@/components/templates/PageLayout";
import { TALK2PAY_PERMISSIONS } from "@/constants/modules.permissions";
import ROUTE_URLS from "@/constants/route-urls";
import useAuth from "@/contexts/auth-context";
import useLanguage from "@/contexts/language-context";
import useGetCustomerPlatforms from "@/hooks/requests/customer-platform/use-get-customer-platforms";
import useGetAccountBalance from "@/hooks/requests/t2Pay/use-get-account-balance";
import useGetCustomerId from "@/hooks/use-get-customer-id";
import { useProtectPage } from "@/hooks/use-protected-page";
import { PlatformAccount } from "@/interfaces/talk2pay.iterfaces";
import { T2PayHomeMessages } from "@/messages/platforms/talk2PayHome/t2pay-home.messages";

import AccountBalanceCard from "./components/AccountBalance";
import CadastralSituation from "./components/CadastralSituation";
import DocumentsTab from "./components/DocumentsTab";
import GeneralInformation from "./components/GeneralInformation";
import { RatesTab } from "./components/RatesTab";
import StatementTab from "./components/StatementTab";
import { T2PayHomeNavigationIds, t2PayHomeNavigationItems } from "./constants";

type Props = {
  messages: T2PayHomeMessages;
  selectedNavigationItem: T2PayHomeNavigationIds;
};

export const STATEMENT_FILTER_STORAGE_KEY = "talk2pay-statement-filter";

const defaultStatementFilter = () => ({
  startDate: dayjs().startOf("day").subtract(7, "day").toDate(),
  endDate: dayjs().endOf("day").toDate(),
});

function getInitialStatementFilter(): { startDate: Date; endDate: Date } {
  try {
    const stored = sessionStorage.getItem(STATEMENT_FILTER_STORAGE_KEY);
    if (stored) {
      const { startDate, endDate } = JSON.parse(stored) as { startDate: string; endDate: string };
      return { startDate: new Date(startDate), endDate: new Date(endDate) };
    }
  } catch {
    // ignore invalid stored value
  }
  return defaultStatementFilter();
}

function Talk2payHome({ messages, selectedNavigationItem }: Props) {
  const { data: balanceData, isLoading: balanceLoading, error } = useGetAccountBalance();
  const customerId = useGetCustomerId();
  const { data: platforms, isLoading: platformsLoading } = useGetCustomerPlatforms({ customerId });
  const { lang } = useLanguage();
  const { checkPermissions } = useAuth();
  const [accountInfo, setAccountInfo] = useState<PlatformAccount | null>(null);
  const [statementFilter, setStatementFilter] = useState(getInitialStatementFilter);

  const handleStatementFilterChange = (startDate: Date, endDate: Date) => {
    setStatementFilter({ startDate, endDate });
    sessionStorage.setItem(
      STATEMENT_FILTER_STORAGE_KEY,
      JSON.stringify({ startDate: startDate.toISOString(), endDate: endDate.toISOString() })
    );
  };

  useEffect(() => {
    if (!platforms || platforms.length === 0) return;

    const platform = platforms.find((p) => p.platformId === 2);
    if (!platform) return;

    const rawInfo =
      (platform as unknown as { PlatformAccountInfo?: string; platformAccountInfo?: string })
        .PlatformAccountInfo ??
      (platform as unknown as { PlatformAccountInfo?: string; platformAccountInfo?: string })
        .platformAccountInfo;

    if (typeof rawInfo !== "string") {
      setAccountInfo(null);
      return;
    }

    const trimmed = rawInfo.trim();
    if (!trimmed) {
      setAccountInfo(null);
      return;
    }

    try {
      const parsed = JSON.parse(trimmed) as unknown;
      if (parsed && typeof parsed === "object") {
        setAccountInfo(parsed as PlatformAccount);
      } else {
        setAccountInfo(null);
      }
    } catch (e) {
      setAccountInfo(null);
    }
  }, [platforms]);

  const isLoading = balanceLoading || platformsLoading;

  const hasNoPermission = checkPermissions(TALK2PAY_PERMISSIONS.VIEW);
  useProtectPage(hasNoPermission);
  const navigationItems = t2PayHomeNavigationItems({ messages });

  const renderTab = (selectedItem: T2PayHomeNavigationIds) => {
    switch (selectedItem) {
      case "statement":
        if (!balanceData) return null;
        return (
          <StatementTab
            messages={messages}
            balanceData={balanceData}
            statementFilterStartDate={statementFilter.startDate}
            statementFilterEndDate={statementFilter.endDate}
            onStatementFilterChange={handleStatementFilterChange}
          />
        );
      case "general-information":
        return (
          <GeneralInformation
            messages={messages}
            isLoading={platformsLoading}
            accountInfo={accountInfo}
          />
        );
      case "documents":
        return <DocumentsTab messages={messages} />;
      case "rates":
        return <RatesTab messages={messages} />;
      case "cadastral-situation":
        return <CadastralSituation messages={messages} />;

      default:
        if (!balanceData) return null;
        return (
          <StatementTab
            messages={messages}
            balanceData={balanceData}
            statementFilterStartDate={statementFilter.startDate}
            statementFilterEndDate={statementFilter.endDate}
            onStatementFilterChange={handleStatementFilterChange}
          />
        );
    }
  };

  const BREAD_CRUMBS = [{ title: messages.breadCrumbTitle, href: ROUTE_URLS.platform(lang) }];

  return (
    <PageLayout messages={messages} title={messages.pageTitle} breadCrumbs={BREAD_CRUMBS}>
      <PageContainer>
        <MDBox>
          <MDBox mb={3}>
            {isLoading ? (
              <Skeleton variant="rectangular" width="100%" height={120} />
            ) : (
              <AccountBalanceCard
                messages={messages}
                data={balanceData}
                accountInfo={accountInfo}
              />
            )}
          </MDBox>

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, lg: 3 }}>
              <NavigationList items={navigationItems} selectedItem={selectedNavigationItem} />
            </Grid>
            <Grid size={{ xs: 12, lg: 9 }}>
              {isLoading ? (
                <Skeleton variant="rectangular" width="100%" height={300} />
              ) : (
                renderTab(selectedNavigationItem)
              )}

              {error && (
                <AlertBlock
                  title={messages.errorTitle}
                  description={messages.errorDescription}
                  alt={messages.errorAlt}
                />
              )}
            </Grid>
          </Grid>
        </MDBox>
      </PageContainer>
    </PageLayout>
  );
}

export default Talk2payHome;
